import { decodeJwt } from "jose";

export type StoredTokens = {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
};

const ACCESS_TOKEN_COOKIE_KEY = "bloodline.auth.access_token";
const REFRESH_TOKEN_COOKIE_KEY = "bloodline.auth.refresh_token";
const EXPIRES_AT_COOKIE_KEY = "bloodline.auth.expires_at";
const FUSIONAUTH_API_URL = import.meta.env.VITE_FUSIONAUTH_URL ?? "https://bloodline-iam.eidra.com";
export const TOKEN_REFRESH_THRESHOLD_MS = 60_000;
export const REFRESH_TOKEN_IDLE_TTL_SECONDS = 30 * 24 * 60 * 60;

export const getTokenPersistenceMaxAges = (expiresAt: number | undefined, now = Date.now()) => {
  const accessToken =
    typeof expiresAt === "number" && Number.isFinite(expiresAt)
      ? Math.floor(Math.max(expiresAt - now, 0) / 1000)
      : undefined;

  return {
    accessToken,
    refreshToken: REFRESH_TOKEN_IDLE_TTL_SECONDS,
    expiryMetadata: REFRESH_TOKEN_IDLE_TTL_SECONDS,
  } as const;
};

let cachedTokens: StoredTokens | null = null;
let refreshPromise: Promise<StoredTokens> | null = null;

const isBrowser = () => typeof window !== "undefined" && typeof document !== "undefined";

const parseCookieValue = (key: string): string | undefined => {
  if (!isBrowser()) {
    return undefined;
  }

  const cookies = document.cookie.split(";");
  const prefix = `${key}=`;

  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }

  return undefined;
};

const getCookieAttributes = (maxAgeSeconds?: number) => {
  const attributes = ["Path=/", "SameSite=Lax"];

  if (typeof maxAgeSeconds === "number" && Number.isFinite(maxAgeSeconds) && maxAgeSeconds >= 0) {
    attributes.push(`Max-Age=${Math.floor(maxAgeSeconds).toString()}`);
  }

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    attributes.push("Secure");
  }

  return attributes.join("; ");
};

const setCookie = (key: string, value: string, maxAgeSeconds?: number) => {
  if (!isBrowser()) {
    return;
  }

  document.cookie = `${key}=${encodeURIComponent(value)}; ${getCookieAttributes(maxAgeSeconds)}`;
};

const clearCookie = (key: string) => {
  if (!isBrowser()) {
    return;
  }

  document.cookie = `${key}=; ${getCookieAttributes(0)}`;
};

const isValidStoredTokens = (data: unknown): data is StoredTokens => {
  if (!data || typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;

  if (obj["accessToken"] && typeof obj["accessToken"] !== "string") return false;
  if (obj["refreshToken"] && typeof obj["refreshToken"] !== "string") return false;
  if (obj["expiresAt"] !== undefined) {
    const exp = obj["expiresAt"];
    if (typeof exp !== "number" || !isFinite(exp) || exp < 0) return false;
  }

  return true;
};

const readFromStorage = (): StoredTokens | null => {
  if (!isBrowser()) {
    return cachedTokens;
  }

  const accessToken = parseCookieValue(ACCESS_TOKEN_COOKIE_KEY);
  const refreshToken = parseCookieValue(REFRESH_TOKEN_COOKIE_KEY);
  const expiresAtRaw = parseCookieValue(EXPIRES_AT_COOKIE_KEY);

  if (!accessToken && !refreshToken) {
    return null;
  }

  const expiresAt = typeof expiresAtRaw === "string" ? Number(expiresAtRaw) : undefined;
  const snapshot: StoredTokens = {
    ...(accessToken ? { accessToken } : {}),
    ...(refreshToken ? { refreshToken } : {}),
    ...(typeof expiresAt === "number" && Number.isFinite(expiresAt) && expiresAt >= 0 ? { expiresAt } : {}),
  };

  if (!isValidStoredTokens(snapshot)) {
    console.warn("Stored tokens are invalid or corrupted, clearing...");
    clearCookie(ACCESS_TOKEN_COOKIE_KEY);
    clearCookie(REFRESH_TOKEN_COOKIE_KEY);
    clearCookie(EXPIRES_AT_COOKIE_KEY);
    cachedTokens = null;
    return null;
  }

  return snapshot;
};

const writeToStorage = (tokens: StoredTokens | null) => {
  if (!isBrowser()) {
    cachedTokens = tokens;
    return;
  }

  if (!tokens || (!tokens.accessToken && !tokens.refreshToken)) {
    clearCookie(ACCESS_TOKEN_COOKIE_KEY);
    clearCookie(REFRESH_TOKEN_COOKIE_KEY);
    clearCookie(EXPIRES_AT_COOKIE_KEY);
    cachedTokens = null;
    return;
  }

  const maxAges = getTokenPersistenceMaxAges(tokens.expiresAt);

  if (tokens.accessToken) {
    setCookie(ACCESS_TOKEN_COOKIE_KEY, tokens.accessToken, maxAges.accessToken);
  } else {
    clearCookie(ACCESS_TOKEN_COOKIE_KEY);
  }

  if (tokens.refreshToken) {
    setCookie(REFRESH_TOKEN_COOKIE_KEY, tokens.refreshToken, maxAges.refreshToken);
  } else {
    clearCookie(REFRESH_TOKEN_COOKIE_KEY);
  }

  if (typeof tokens.expiresAt === "number" && Number.isFinite(tokens.expiresAt)) {
    setCookie(EXPIRES_AT_COOKIE_KEY, tokens.expiresAt.toString(), maxAges.expiryMetadata);
  } else {
    clearCookie(EXPIRES_AT_COOKIE_KEY);
  }

  cachedTokens = tokens;
};

export const deriveExpiresAt = (token?: string) => {
  if (!token) {
    return undefined;
  }

  try {
    const decoded = decodeJwt(token);
    return typeof decoded.exp === "number" ? decoded.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

export const getStoredTokens = (): StoredTokens | null => {
  cachedTokens = cachedTokens ?? readFromStorage();
  return cachedTokens;
};

export const persistTokens = (tokens: StoredTokens | null) => {
  if (!tokens) {
    writeToStorage(null);
    return;
  }

  const existing = getStoredTokens();
  const merged: StoredTokens = {};

  const accessToken = tokens.accessToken ?? existing?.accessToken;
  if (accessToken) {
    merged.accessToken = accessToken;
  }

  const refreshToken = tokens.refreshToken ?? existing?.refreshToken;
  if (refreshToken) {
    merged.refreshToken = refreshToken;
  }

  const expiresAt = tokens.expiresAt ?? existing?.expiresAt ?? (accessToken ? deriveExpiresAt(accessToken) : undefined);
  if (typeof expiresAt === "number") {
    merged.expiresAt = expiresAt;
  }

  writeToStorage(Object.keys(merged).length > 0 ? merged : null);
};

export const clearTokens = () => {
  writeToStorage(null);
};

type RefreshResponse = {
  access_token: string;
  refresh_token?: string;
};

const performRefresh = async (refreshToken: string): Promise<StoredTokens> => {
  const appId = localStorage.getItem("bloodline.auth.appId") ?? "";
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
    client_id: appId,
  });

  const response = await fetch(`${FUSIONAUTH_API_URL}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`Failed to refresh access token (${String(response.status)})`);
  }

  const payload = (await response.json()) as RefreshResponse;
  const accessToken = payload.access_token;
  const newRefreshToken = payload.refresh_token ?? refreshToken;
  const expiresAt = deriveExpiresAt(accessToken);

  const snapshot: StoredTokens = {
    accessToken,
    refreshToken: newRefreshToken,
  };

  if (typeof expiresAt === "number") {
    snapshot.expiresAt = expiresAt;
  }

  writeToStorage(snapshot);
  return snapshot;
};

export const refreshTokens = (options?: { refreshToken?: string }) => {
  refreshPromise ??= (async () => {
    const stored = getStoredTokens();
    const refreshToken = options?.refreshToken ?? stored?.refreshToken;

    if (!refreshToken) {
      throw new Error("Missing refresh token");
    }

    try {
      return await performRefresh(refreshToken);
    } catch (error) {
      clearTokens();
      throw error;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export const getValidAccessToken = async (threshold = TOKEN_REFRESH_THRESHOLD_MS): Promise<string | null> => {
  const stored = getStoredTokens();

  if (!stored?.accessToken) {
    return null;
  }

  if (stored.refreshToken && typeof stored.expiresAt === "number" && stored.expiresAt - Date.now() <= threshold) {
    const refreshed = await refreshTokens();
    return refreshed.accessToken ?? null;
  }

  return stored.accessToken;
};
